import React from "react";

import cx from "classnames";

import { BRANDING_CLASSES } from "../../styles/branding";
import { Button } from "../Button";
import { Card } from "../Card";
import { IconDictionary } from "../Icon";
import { IconButton } from "../IconButton";
import { SlideIntoView } from "../SlideIntoView";

import styles from "./TogglePanel.scss";

export interface TogglePanelProps {
  /**
   * The icon to use on the feature button.
   */
  buttonIcon?: IconDictionary;
  /**
   * The button text to use for the feature button
   */
  buttonText?: string;
  /**
   * Whether or not to show the form. Default: `false`
   */
  isOpen?: boolean;
  /**
   * Function passed in to handle the form closing/opening itself. Takes one argument: the open state (`true` = open, `false` = closed).
   */
  onSetOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Teaser text shown to the left of the feature button
   */
  teaserText?: React.ReactNode;
  teaserClassName?: string;
  /**
   * If given, button will do a `window.location.href` to the url given
   */
  href?: string;
  /**
   * If href URL is passed, we use this link element. Default: "a"
   */
  linkElement?: "a" | React.ElementType;
  /**
   * Accessibility aria label for the close icon
   */
  closeIconAriaLabel?: string;
  /**
   * ID for the toggled panel
   */
  id?: string;
  /**
   * An accessible label for the card. It will be visible for screenreaders that enter it.
   */
  cardAriaLabel?: string;
  /**
   * Function to call when the toggle panel is closed.
   */
  onClose?: () => void;
}

/**
 * A toggle panel for large forms and other tall features that need to be expandable/contractable.
 */
function TogglePanel({
  children,
  buttonText,
  buttonIcon,
  isOpen = false,
  onSetOpen,
  teaserClassName = "",
  teaserText,
  href,
  linkElement = "a",
  closeIconAriaLabel = "",
  id,
  cardAriaLabel,
  onClose,
  ...wrapperProps
}: React.PropsWithChildren<TogglePanelProps>) {
  const closeButtonRef = React.useRef<HTMLButtonElement>();
  const toggleButtonRef = React.useRef<HTMLButtonElement>();
  const openRef = React.useRef(!!isOpen);
  const previousOpenStatus = openRef.current;

  const handleOpen = React.useCallback(() => {
    onSetOpen?.(true);
  }, [onSetOpen]);

  const handleClose = React.useCallback(() => {
    onSetOpen?.(false);
    onClose?.();
  }, [onSetOpen, onClose]);

  React.useEffect(() => {
    if (!previousOpenStatus && isOpen) {
      closeButtonRef.current?.focus();
    } else if (!!previousOpenStatus && !isOpen) {
      toggleButtonRef.current?.focus();
    }
    openRef.current = isOpen;
  }, [isOpen]);

  const buttonProps: Record<string, any> = React.useMemo(() => {
    const buttonProps: Record<string, any> = {
      variant: "feature_inverted",
      icon: buttonIcon,
      "data-testid": "toggle-panel-open-button",
    };
    if (href && linkElement === "a") {
      buttonProps.linkElement = "a";
      buttonProps.href = href;
    } else if (href && linkElement !== "a") {
      buttonProps.linkElement = linkElement;
      buttonProps.to = href;
    } else {
      buttonProps.onClick = handleOpen;
    }
    return buttonProps;
  }, [linkElement, href, handleOpen]);

  return (
    <div className={styles.wrapper} {...wrapperProps}>
      <Card
        className={cx(
          styles.controls,
          BRANDING_CLASSES.PRIMARY_COLOR_BACKGROUND,
          {
            [styles.controlsShown]: !isOpen,
            [teaserClassName]: teaserClassName && !isOpen,
          }
        )}
        background="two-tone"
        data-testid="toggle-panel-controls"
        aria-hidden={Boolean(isOpen)}
      >
        {Boolean(teaserText) && (
          <div
            className={cx(
              styles.teaser,
              BRANDING_CLASSES.OFFSET_PRIMARY_COLOR_FOREGROUND
            )}
            data-testid="toggle-panel-teaser"
          >
            {teaserText}
          </div>
        )}
        <div className={styles.buttonFeature}>
          <Button
            {...buttonProps}
            aria-expanded={isOpen}
            aria-controls={id}
            innerRef={toggleButtonRef}
          >
            {buttonText}
          </Button>
        </div>
      </Card>
      <SlideIntoView open={isOpen} id={id}>
        <div
          className={cx(styles.childContent, {
            [styles.childContentShown]: true,
          })}
          data-testid="toggle-panel-content"
          aria-hidden={Boolean(!isOpen)}
        >
          <Card className={styles.childContentCard} aria-label={cardAriaLabel}>
            <IconButton
              innerRef={closeButtonRef}
              icon="close"
              className={styles.closeButton}
              variant="transparent"
              onClick={handleClose}
              data-testid="toggle-panel-close-button"
              ariaLabel={closeIconAriaLabel}
            />
            {children}
          </Card>
        </div>
      </SlideIntoView>
    </div>
  );
}

export default TogglePanel;
